/**
 * Realtime event name constants and Zod schemas for every incoming payload.
 *
 * # Naming reality: live vs target
 *
 * The backend playbook (hostesa-backend/docs/frontend/04-realtime.md) documents
 * the LIVE event names emitted by the current Socket.IO gateway:
 *
 *   call.status.changed       – live, hotel room
 *   call.handoff.requested    – live, hotel room
 *   call.transcript.final     – live, call room
 *   reservation.changed       – live, hotel room
 *   offer.changed             – live, hotel room
 *   booking.changed           – live, hotel room
 *   task.changed              – live, hotel room
 *   knowledge.gap.detected    – live, hotel room
 *
 * The architecture deep-dive (docs/architecture/03-hostesa-app/04-realtime-client.md)
 * defines the TARGET canonical names (`hostesa.<domain>.<event>.v1`). These are NOT
 * yet live — they will be introduced as the contracts layer matures. When that
 * migration happens this file should:
 *   1. Register the new v1 event names alongside.
 *   2. Route both names to the same handler.
 *   3. Remove the legacy names once the backend drains old consumers.
 *
 * TARGET names (not live — compatibility reference only):
 *   hostesa.call.status.changed.v1
 *   hostesa.call.transcript.partial.v1
 *   hostesa.call.transcript.final.v1
 *   hostesa.call.ai.tool.started.v1
 *   hostesa.call.ai.tool.completed.v1
 *   hostesa.call.handoff.requested.v1
 *   hostesa.call.recording.available.v1
 *   hostesa.notification.created.v1
 *
 * Implement the LIVE shape. This file is the trust boundary — validate at the edge,
 * never trust the network.
 */
import { z } from 'zod';

// ---------------------------------------------------------------------------
// Envelope — every event carries this outer wrapper
// ---------------------------------------------------------------------------

/**
 * Common envelope that wraps every realtime event. The `type` field mirrors
 * the Socket.IO event name so the payload is self-describing.
 */
export const realtimeEnvelopeSchema = z.object({
  type: z.string(),
  tenantId: z.string(),
  hotelId: z.string(),
  callId: z.string().optional(),
});
export type RealtimeEnvelope = z.infer<typeof realtimeEnvelopeSchema>;

// ---------------------------------------------------------------------------
// Live event names (current backend gateway)
// ---------------------------------------------------------------------------

export const LIVE_EVENTS = {
  /** Call entered a new lifecycle state. Hotel room. */
  CALL_STATUS_CHANGED: 'call.status.changed',
  /** AI escalated to a human operator. Hotel room. */
  CALL_HANDOFF_REQUESTED: 'call.handoff.requested',
  /** Completed transcript segment for a live call. Call room only. */
  CALL_TRANSCRIPT_FINAL: 'call.transcript.final',
  /** A reservation row changed. Hotel room. */
  RESERVATION_CHANGED: 'reservation.changed',
  /** An offer changed. Hotel room. */
  OFFER_CHANGED: 'offer.changed',
  /** A booking row changed. Hotel room. */
  BOOKING_CHANGED: 'booking.changed',
  /** An operations task changed. Hotel room. */
  TASK_CHANGED: 'task.changed',
  /** A knowledge gap was detected. Hotel room. */
  KNOWLEDGE_GAP_DETECTED: 'knowledge.gap.detected',
} as const;
export type LiveEventName = (typeof LIVE_EVENTS)[keyof typeof LIVE_EVENTS];

// ---------------------------------------------------------------------------
// Target event names (not yet live — compatibility reference)
// ---------------------------------------------------------------------------

export const TARGET_EVENTS = {
  /** @future Canonical target name for call.status.changed */
  CALL_STATUS_CHANGED_V1: 'hostesa.call.status.changed.v1',
  /** @future Canonical target name for streaming transcript partial */
  CALL_TRANSCRIPT_PARTIAL_V1: 'hostesa.call.transcript.partial.v1',
  /** @future Canonical target name for call.transcript.final */
  CALL_TRANSCRIPT_FINAL_V1: 'hostesa.call.transcript.final.v1',
  /** @future Canonical target name for AI tool started */
  CALL_AI_TOOL_STARTED_V1: 'hostesa.call.ai.tool.started.v1',
  /** @future Canonical target name for AI tool completed */
  CALL_AI_TOOL_COMPLETED_V1: 'hostesa.call.ai.tool.completed.v1',
  /** @future Canonical target name for call.handoff.requested */
  CALL_HANDOFF_REQUESTED_V1: 'hostesa.call.handoff.requested.v1',
  /** @future Canonical target name for recording available */
  CALL_RECORDING_AVAILABLE_V1: 'hostesa.call.recording.available.v1',
  /** @future General notification */
  NOTIFICATION_CREATED_V1: 'hostesa.notification.created.v1',
  /** @future Canonical target name for task.changed */
  TASK_UPDATED_V1: 'hostesa.task.updated.v1',
} as const;
export type TargetEventName = (typeof TARGET_EVENTS)[keyof typeof TARGET_EVENTS];

// ---------------------------------------------------------------------------
// Per-event payload schemas
// ---------------------------------------------------------------------------

/** `call.status.changed` — hint: status string. Invalidate; no canonical shape. */
export const callStatusChangedDataSchema = z.object({
  status: z.string(),
});
export const callStatusChangedEventSchema = realtimeEnvelopeSchema.extend({
  type: z.literal(LIVE_EVENTS.CALL_STATUS_CHANGED),
  callId: z.string(),
  data: callStatusChangedDataSchema,
});
export type CallStatusChangedEvent = z.infer<typeof callStatusChangedEventSchema>;

/** `call.handoff.requested` — hint only. */
export const callHandoffRequestedDataSchema = z.object({
  reason: z.string().optional(),
});
export const callHandoffRequestedEventSchema = realtimeEnvelopeSchema.extend({
  type: z.literal(LIVE_EVENTS.CALL_HANDOFF_REQUESTED),
  callId: z.string(),
  data: callHandoffRequestedDataSchema,
});
export type CallHandoffRequestedEvent = z.infer<typeof callHandoffRequestedEventSchema>;

/**
 * `call.transcript.final` — complete transcript segment. Delivered to call room.
 * Payload is authoritative enough to append directly to the transcript cache.
 */
export const callTranscriptFinalDataSchema = z.object({
  segmentId: z.string(),
  seq: z.number().int().nonnegative(),
  speaker: z.string(),
  text: z.string(),
  language: z.string().optional(),
});
export const callTranscriptFinalEventSchema = realtimeEnvelopeSchema.extend({
  type: z.literal(LIVE_EVENTS.CALL_TRANSCRIPT_FINAL),
  callId: z.string(),
  data: callTranscriptFinalDataSchema,
});
export type CallTranscriptFinalEvent = z.infer<typeof callTranscriptFinalEventSchema>;

/** `reservation.changed` — hint. Invalidate reservation row. */
export const reservationChangedDataSchema = z.object({
  id: z.string(),
  status: z.string().optional(),
  eventType: z.string().optional(),
});
export const reservationChangedEventSchema = realtimeEnvelopeSchema.extend({
  type: z.literal(LIVE_EVENTS.RESERVATION_CHANGED),
  data: reservationChangedDataSchema,
});
export type ReservationChangedEvent = z.infer<typeof reservationChangedEventSchema>;

/** `offer.changed` — hint. Invalidate offer row. */
export const offerChangedDataSchema = z.object({
  id: z.string(),
  status: z.string().optional(),
  eventType: z.string().optional(),
});
export const offerChangedEventSchema = realtimeEnvelopeSchema.extend({
  type: z.literal(LIVE_EVENTS.OFFER_CHANGED),
  data: offerChangedDataSchema,
});
export type OfferChangedEvent = z.infer<typeof offerChangedEventSchema>;

/** `booking.changed` — hint. Invalidate booking row. */
export const bookingChangedDataSchema = z.object({
  id: z.string(),
  status: z.string().optional(),
  eventType: z.string().optional(),
});
export const bookingChangedEventSchema = realtimeEnvelopeSchema.extend({
  type: z.literal(LIVE_EVENTS.BOOKING_CHANGED),
  data: bookingChangedDataSchema,
});
export type BookingChangedEvent = z.infer<typeof bookingChangedEventSchema>;

/** `task.changed` — hint. Invalidate task row and tasks list. */
export const taskChangedDataSchema = z.object({
  id: z.string(),
  status: z.string().optional(),
  eventType: z.string().optional(),
});
export const taskChangedEventSchema = realtimeEnvelopeSchema.extend({
  type: z.literal(LIVE_EVENTS.TASK_CHANGED),
  data: taskChangedDataSchema,
});
export type TaskChangedEvent = z.infer<typeof taskChangedEventSchema>;

/** `knowledge.gap.detected` — hint. Badge the knowledge-gaps inbox. */
export const knowledgeGapDetectedDataSchema = z.object({
  id: z.string(),
  status: z.string().optional(),
  eventType: z.string().optional(),
});
export const knowledgeGapDetectedEventSchema = realtimeEnvelopeSchema.extend({
  type: z.literal(LIVE_EVENTS.KNOWLEDGE_GAP_DETECTED),
  data: knowledgeGapDetectedDataSchema,
});
export type KnowledgeGapDetectedEvent = z.infer<typeof knowledgeGapDetectedEventSchema>;

// ---------------------------------------------------------------------------
// Union type for all known events
// ---------------------------------------------------------------------------

export type AnyRealtimeEvent =
  | CallStatusChangedEvent
  | CallHandoffRequestedEvent
  | CallTranscriptFinalEvent
  | ReservationChangedEvent
  | OfferChangedEvent
  | BookingChangedEvent
  | TaskChangedEvent
  | KnowledgeGapDetectedEvent;
